<template>
  <section v-if="!isLoading" class="pass-analytics">
    <h2 class="dashboard__tab-title">My Analytics</h2>
    <h5 class="dashboard__tab-sub-title">
      Current month vs previous month
    </h5>
    <StatCards :stats="cardStats" />
    <MinutesWatchedChart
      v-if="formattedChart && Object.keys(formattedChart).length > 1"
      :data="getChartData()"
    />
    <div class="course-history-container">
      <CourseHistory
        v-if="historyCourses"
        :class="[
          'course-history-container',
          isLoadingCourses && 'course-history-container--loading',
        ]"
        :courses="historyCourses"
        :totalCourses="totalCourses"
        @update-current-page-number="getCoursesForHistory"
        @update-current-page-items="getCoursesForHistory"
      />
      <div
        v-if="isLoadingCourses"
        class="course-history-container__loading-wrapper"
      >
        <ActionLoader class="course-history-container--spinner" />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import StatCards from '@/components/user-dashboard/pass-analytics/StatCards.vue'
import MinutesWatchedChart from '@/components/user-dashboard/pass-analytics/MinutesWatchedChart.vue'
import CourseHistory from '@/components/user-dashboard/pass-analytics/CourseHistory.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'

export default {
  name: 'PassAnalytics',
  components: {
    StatCards,
    MinutesWatchedChart,
    CourseHistory,
    ActionLoader,
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      courseIds: {},
      isLoadingCourses: false,
    }
  },
  computed: {
    ...mapState({
      courses: state => state.statistics.coursesForHistory,
      monthlyStats: state => state.statistics.monthlyStats,
      yearlyStats: state => state.statistics.yearlyStats,
      lastMonthStats: state => state.statistics.lastMonthStats,
      activeCourses: state => state.courses.activeCourses,
    }),
    ...mapGetters({
      courseHistory: 'statistics/courseHistoryStats',
      formattedChart: 'statistics/formattedChart',
    }),
    historyCourses() {
      return this.courses?.results
    },
    totalCourses() {
      return this.courses?.count
    },
    cardStats() {
      const { courses_viewed, time_watched, videos_watched } = this.yearlyStats
      const { time_watched: monthly_time_watched } = this.monthlyStats
      let data = {}
      data = {
        totalCourses: {
          label: 'Courses viewed',
          currentMonth: courses_viewed?.value ?? 0,
          lastMonth: this.lastMonthStats.courses_viewed?.value ?? 0,
        },
        totalTime: {
          label: 'Total time watched',
          currentMonth: this.getTimeValue(time_watched?.value),
          lastMonth:
            this.getTimeValue(this.lastMonthStats.time_watched?.value) ?? 0,
        },
        numVideosWatched: {
          label: 'Videos viewed',
          currentMonth: videos_watched?.value ?? 0,
          lastMonth: this.lastMonthStats.videos_watched?.value ?? 0,
        },
        monthTimeWatched: {
          label: 'Watched this month',
          currentMonth: this.getTimeValue(monthly_time_watched?.value),
          lastMonth:
            this.getTimeValue(
              this.lastMonthStats.monthly_time_watched?.value
            ) ?? 0,
        },
      }
      return data
    },
    chartMonths() {
      return this.formattedChart.map(month => month.month)
    },
    chartMonthsLabel() {
      return this.formattedChart.map(month => month.monthLabel)
    },
    chartValues() {
      return this.formattedChart.map(month => Math.floor(month.value / 60))
    },
  },
  methods: {
    ...mapActions('statistics', [
      'getMonthlyStats',
      'getYearlyStats',
      'getCourseHistoryStats',
      'getLastMonthStats',
    ]),
    ...mapActions('courses', ['getActiveCourses']),

    getChartData() {
      let chartData = {
        values: this.chartValues,
        months: this.chartMonths,
        monthsLabel: this.chartMonthsLabel,
        labels: {},
      }
      chartData = {
        ...chartData,
        labels: {
          title: 'Minutes Watched',
          yAxes: 'Number of Minutes Watched',
          xAxes: 'Minutes watched for each month over the last year.',
        },
      }
      return chartData
    },
    async getAllStats() {
      const promises = [
        this.getMonthlyStats(),
        this.getYearlyStats(),
        this.getCourseHistoryStats(),
        this.getLastMonthStats(),
        this.getActiveCourses(),
      ]
      await Promise.allSettled(promises)
      if (this.activeCourses.length) {
        const courseIds = this.activeCourses.reduce((acc, { id }) => {
          if (id) return acc.concat(id.slice(0, 8))
          return acc
        }, [])
        if (courseIds?.length) {
          this.courseIds = courseIds
          await this.getCoursesForHistory(10, 1)
        }
      }
    },
    async getCoursesForHistory(page_size, page) {
      if (this.courseIds?.length) {
        this.isLoadingCourses = true
        await this.$store.dispatch('statistics/getCoursesForHistory', {
          courseIds: this.courseIds,
          page_size: page_size,
          page: page,
          ordering: '-last_activity_date',
        })
        this.isLoadingCourses = false
      }
    },
    getTimeValue(time) {
      const value = time ?? 0
      if (value > 0 && value < 60) return 1
      return value / 60
    },
  },
  async created() {
    await this.getAllStats()
    this.$store.dispatch('app/setIsComponentLoading', false)
  },
}
</script>

<style lang="scss">
.course-history-container {
  position: relative;
  &--loading {
    opacity: 0.5;
  }
  &--spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  &__loading-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    background: transparent;
  }
}
</style>
